// Self-contained "Overview dashboard" widget. Composes the community entity
// pieces (HealthGauge, MetricCard, ActivityChart) and the message entity
// piece (TopPostsList) against the live overview + health responses.
//
// This widget does no data fetching itself — the page above passes in the
// already-resolved responses. That split keeps the widget render-pure
// (re-renders cheaply when only one of the two queries refetches) and lets
// future surfaces (a tablet split-view, a PDF export) reuse it without the
// React Query plumbing.

import type {
  CommunityHealthResponse,
  CommunityOverviewResponse,
} from "@community-analytics/shared";

import {
  ActivityChart,
  HealthGauge,
  MetricCard,
  formatCompactNumber,
  formatSignedDelta,
} from "@entities/community";
import { TopPostsList } from "@entities/message";
import { cn } from "@shared/lib";

import { OVERVIEW_STRINGS } from "../lib/strings";

import { OverviewEmptyState } from "./OverviewEmptyState";

export interface OverviewGridProps {
  overview: CommunityOverviewResponse;
  health: CommunityHealthResponse;
  className?: string;
}

export function OverviewGrid({
  overview,
  health,
  className,
}: OverviewGridProps) {
  // Empty state: the API tells us when there's <1h of data. We trust the
  // flag rather than re-deriving from the trend (which is zero-filled and
  // therefore looks "empty" even on day-1 with one message).
  if (!overview.dataAvailable) {
    return (
      <OverviewEmptyState
        className={className}
        title={OVERVIEW_STRINGS.empty.title}
        body={OVERVIEW_STRINGS.empty.body}
      />
    );
  }

  // Pre-compute display metrics from the overview response. Average daily
  // messages over the 7-day trend is more informative than the latest hour
  // bucket alone — the latest hour is often partial.
  const totalMessagesWeek = overview.trend.reduce(
    (sum, p) => sum + p.messagesTotal,
    0,
  );
  const avgMessagesPerDay = Math.round(totalMessagesWeek / 7);

  const captionLastUpdated = `${OVERVIEW_STRINGS.health.captionLastUpdated} ${formatRelative(
    overview.generatedAt,
  )}`;

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      <section aria-labelledby="overview-health" className="flex flex-col">
        <h2 id="overview-health" className="sr-only">
          {OVERVIEW_STRINGS.health.cardTitle}
        </h2>
        <div className="flex flex-col items-center gap-2">
          <HealthGauge
            score={health.healthScore}
            label={OVERVIEW_STRINGS.health.label}
            color={health.color}
            caption={captionLastUpdated}
          />
          {health.upgradeRequired ? (
            <p className="text-xs text-muted-foreground">
              {health.upgradeCta || OVERVIEW_STRINGS.health.upgradeCta}
            </p>
          ) : null}
        </div>
      </section>

      <section
        aria-label="Key metrics"
        className="grid grid-cols-2 gap-3"
      >
        <MetricCard
          label={OVERVIEW_STRINGS.metrics.membersLabel}
          value={formatCompactNumber(overview.memberCountCached)}
          hint={OVERVIEW_STRINGS.metrics.membersHint}
        />
        <MetricCard
          label={OVERVIEW_STRINGS.metrics.dauLabel}
          value={formatCompactNumber(overview.current?.activeMembers ?? 0)}
          hint={OVERVIEW_STRINGS.metrics.dauHint}
        />
        <MetricCard
          label={OVERVIEW_STRINGS.metrics.messagesLabel}
          value={formatCompactNumber(avgMessagesPerDay)}
          hint={OVERVIEW_STRINGS.metrics.messagesHint}
        />
        <MetricCard
          label={OVERVIEW_STRINGS.metrics.growthLabel}
          value={formatSignedDelta(overview.netGrowth24h)}
          hint={OVERVIEW_STRINGS.metrics.growthHint}
          tone={
            overview.netGrowth24h > 0
              ? "positive"
              : overview.netGrowth24h < 0
                ? "negative"
                : "neutral"
          }
        />
      </section>

      <section aria-labelledby="overview-trend" className="flex flex-col gap-2">
        <h2 id="overview-trend" className="text-sm font-semibold">
          {OVERVIEW_STRINGS.trend.title}
        </h2>
        <ActivityChart
          points={overview.trend}
          emptyLabel={OVERVIEW_STRINGS.trend.empty}
        />
      </section>

      <section aria-labelledby="overview-top-posts" className="flex flex-col gap-2">
        <div>
          <h2
            id="overview-top-posts"
            className="text-sm font-semibold"
          >
            {OVERVIEW_STRINGS.topPosts.title}
          </h2>
          <p className="text-xs text-muted-foreground">
            {OVERVIEW_STRINGS.topPosts.subtitle}
          </p>
        </div>
        <TopPostsList
          posts={overview.topPosts}
          emptyLabel={OVERVIEW_STRINGS.topPosts.empty}
          reactionsLabel={OVERVIEW_STRINGS.topPosts.reactions}
          repliesLabel={OVERVIEW_STRINGS.topPosts.replies}
        />
      </section>
    </div>
  );
}

/**
 * Lightweight relative time (no date-fns dependency for one site). Outputs
 * "just now" / "2 min ago" / "3 h ago" / a fallback ISO. The Overview page
 * refetches every 30s so anything older than ~5 min is stale data.
 */
function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.round((now - then) / 1000));
  if (diffSec < 30) return "just now";
  if (diffSec < 60) return `${diffSec}s ago`;
  const minutes = Math.round(diffSec / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return iso;
}
